import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button } from '@mui/material';
import { SystemTable } from '@sharedCR/common/SystemTable';
import { AddBoxIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { PAGE_CODES } from 'constants/pageCodes';
import useCompanyId from 'hooks/useCompanyId';
import useSort from 'hooks/useSort';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openConfirmationDialogAction, openDialogAction } from 'store/global/actions';
import { updateFeeProviderAction } from 'store/system/actions';
import { selectFeeProviderById } from 'store/system/selectors';

import FeeProviderLocationForm from './FeeProviderLocationForm';
import { tableConfig } from './FeeProviderLocationTable.config';
import { SORT_ORDER } from './FeeProviderLocationTable.constants';
import { addButtonWrapperSx } from './FeeProviderLocationTable.styles';
import { FeeProviderLocationTableTestIds } from './FeeProviderLocationTable.testIds';
import { getFormattedTableData } from './FeeProviderLocationTable.utils';

const FeeProviderLocationTable = ({
  setIsLoading,
  isLoading,
  feeProviderId = '',
  onChangeLocationCallback,
  onLocationRowCustomClick,
}) => {
  const dispatch = useDispatch();
  const companyId = useCompanyId();

  const { order, onRequestSort } = useSort(SORT_ORDER);

  const feeProvider = useSelector(selectFeeProviderById(feeProviderId));
  const { isManageAllowed } = useSelector(getMasterPermissionsToPage(PAGE_CODES.FEE_PROVIDER));

  const isCompanyManageAllowed = !feeProviderId || companyId === feeProvider?.companyId;

  const isFormDisabled = !isManageAllowed || isLoading || !isCompanyManageAllowed;

  const openForm = useCallback(
    locationId => {
      const isEdit = !!locationId && _.isString(locationId);

      dispatch(
        openDialogAction({
          header: `${isEdit ? 'Edit' : 'Add'} provider location`,
          size: ModalSize.XL,
          afterCloseCallback: onChangeLocationCallback,
          body: dialogProps => (
            <FeeProviderLocationForm
              feeProviderId={feeProviderId}
              feeProviderLocationId={isEdit ? locationId : ''}
              onChangeLocationCallback={onChangeLocationCallback}
              {...dialogProps}
            />
          ),
        }),
      );
    },
    [dispatch, feeProviderId, onChangeLocationCallback],
  );

  const onDeleteProviderLocation = useCallback(
    locationId => {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'Confirm Fee Provider Location delete',
          description: 'Are you sure you want to delete this location?',
          onAccept: () => {
            setIsLoading(true);

            dispatch(
              updateFeeProviderAction({
                feeProviderId,
                feeProviderData: feeProvider,
                remove: [locationId],
              }),
            )
              .catch(Error)
              .finally(() => setIsLoading(false));
          },
        }),
      );
    },
    [dispatch, setIsLoading, feeProvider, feeProviderId],
  );

  const orderedLocations = useMemo(
    () =>
      _.orderBy(
        feeProvider.locations,
        [location => (location[order.id] || '').toString().toLowerCase()],
        [order.direction],
      ),
    [feeProvider.locations, order],
  );

  const formattedData = useMemo(
    () =>
      getFormattedTableData(orderedLocations, {
        isFormDisabled,
        isLoading,
        onEdit: openForm,
        onDelete: onDeleteProviderLocation,
      }),
    [orderedLocations, isFormDisabled, isLoading, openForm, onDeleteProviderLocation],
  );

  const onRowClick = useCallback(
    id => {
      if (onLocationRowCustomClick) {
        const location = _.find(feeProvider.locations, loc => loc.feeProviderLocationId === id);

        return onLocationRowCustomClick(null, { location });
      }

      if (isFormDisabled) {
        return;
      }

      openForm(id);
    },
    [openForm, onLocationRowCustomClick, feeProvider.locations, isFormDisabled],
  );

  return (
    <>
      {!isFormDisabled && (
        <Box sx={addButtonWrapperSx}>
          <Button
            data-testid={FeeProviderLocationTableTestIds.ADD_PROVIDER_LOCATION_BUTTON}
            disabled={isFormDisabled}
            startIcon={<AddBoxIcon />}
            variant="contained"
            onClick={openForm}
          >
            Add
          </Button>
        </Box>
      )}

      <SystemTable
        config={tableConfig}
        data={formattedData}
        isLoading={isLoading}
        order={order}
        skeletonRows={3}
        testId={FeeProviderLocationTableTestIds.PROVIDER_LOCATION_TABLE}
        onRequestSort={onRequestSort}
        onRowClick={onRowClick}
      />
    </>
  );
};

FeeProviderLocationTable.propTypes = {
  feeProviderId: PropTypes.string,
  onChangeLocationCallback: PropTypes.func,
  onLocationRowCustomClick: PropTypes.func,
  ...WithLoadingPropTypes,
};

export default FeeProviderLocationTable;
